import Misc from 'src/utils/helper/misc';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import * as Common from 'src/utils/constants/common';
import { useRoute } from 'vue-router';

export function useAiTextEditorValidations() {
  const store = useStore();
  const { t } = useI18n();
  const route = useRoute();

  /**
   * The function `checkForExtraSpaces` in JavaScript checks for extra spaces in a text, trims out
   * leading and trailing spaces, and identifies words surrounding the extra spaces.
   * @returns The function `checkForExtraSpaces` is returning a string that includes the error message
   * key `'pages.aiTextEditor.errors.extra_spaces'` concatenated with the cleaned and non-empty contexts
   * found in the text with extra spaces, joined by commas and ending with a period.
   */
  const checkForExtraSpaces = text => {
    // check for extra spaces in the text, trim out the before and after spaces
    let sanitizedText = text.trim();
    sanitizedText = sanitizedText.replace(Common.REGEX_COLLECTIONS.SPACE_UNICODE, ' ');
    sanitizedText = sanitizedText.replace(Common.REGEX_COLLECTIONS.SPACE_ESCAPED, ' ');

    const extraSpacesMatches = [];
    const spacePattern = new RegExp(Common.REGEX_COLLECTIONS.EXTRA_SPACES_PATTERN);
    let match;

    while ((match = spacePattern.exec(sanitizedText)) !== null) {
      const spaceStartIndex = match.index;
      const spaceEndIndex = spaceStartIndex + match[0].length;

      const textBefore = sanitizedText.substring(0, spaceStartIndex);
      const wordBefore = textBefore.split(/\s+/).pop();

      const textAfter = sanitizedText.substring(spaceEndIndex);
      const wordAfter = textAfter.split(/\s+/).shift();

      if (wordBefore && wordAfter) {
        extraSpacesMatches.push(`${wordBefore}${match[0]}${wordAfter}`);
      }
    }

    if (extraSpacesMatches.length > 0) {
      const cleanedContext = extraSpacesMatches.map(context =>
        context.replace(Common.REGEX_COLLECTIONS.HTML_TAG_PATTERN, '').trim()
      );
      const nonEmptyContext = cleanedContext.filter(Boolean);
      if (nonEmptyContext.length > 0) {
        return t('pages.aiTextEditor.errors.extra_spaces') + nonEmptyContext.join(', ') + '.';
      }
    }
  };

  /**
   * The function `checkMinMaxLength` checks if a given text length meets minimum and maximum limits,
   * providing an error message and error code if applicable.
   * @param textLength - The `textLength` parameter in the `checkMinMaxLength` function represents the
   * length of the text that needs to be checked against certain limits.
   * @param [minLimit] - The `minLimit` parameter in the `checkMinMaxLength` function represents the
   * minimum character limit allowed for the text input. It is used to check if the length of the text is
   * greater than or equal to this minimum limit. If the text length is less than the `minLimit`, an
   * error
   * @param [textLimit=null] - The `textLimit` parameter in the `checkMinMaxLength` function represents
   * the maximum character limit allowed for the text input. If the `textLength` exceeds this
   * `textLimit`, an error message will be generated indicating that the text is too long.
   * @param [isAiEnabled=false] - The `isAiEnabled` parameter in the `checkMinMaxLength` function is a
   * boolean flag that indicates whether AI (Artificial Intelligence) is enabled or not. It is used to
   * determine which error message to display based on whether AI is enabled or disabled.
   * @param [moduleName=description] - The `moduleName` parameter in the `checkMinMaxLength` function is
   * used to specify the name of the module for which the text length is being checked. It is a string
   * parameter with a default value of `'description'`. This parameter is used in the error messages to
   * indicate which module's text length
   * @returns The function `checkMinMaxLength` returns an object with two properties: `errorMessage` and
   * `errorCode`.
   */
  function checkMinMaxLength(
    textLength,
    minLimit = Common.DESCRIPTION_CHAR_LIMITS.minCharLimit,
    textLimit = null,
    isAiEnabled = false,
    moduleName = 'description'
  ) {
    let errorMessage = null,
      errorCode = '';

    const limit = isAiEnabled ? Common.MIN_LENGTH_AI_ENABLED : minLimit;

    if (textLength < limit) {
      errorMessage = isAiEnabled
        ? t('pages.aiTextEditor.errors.min_length_ai_enabled', { limit })
        : t('pages.aiTextEditor.errors.min_length_ai_disabled', {
          limit,
          moduleName: Misc.capitalize(moduleName)
        });
    }

    if (textLimit && textLength > textLimit) {
      errorCode = 'TEXT_TOO_LONG';
      errorMessage = isAiEnabled
        ? t('pages.aiTextEditor.errors.max_length_ai_enabled', {
          limit: textLimit,
          moduleName: Misc.capitalize(moduleName)
        })
        : t('pages.aiTextEditor.errors.max_length_ai_disabled', { limit: textLimit });
    }
    return { errorMessage, errorCode };
  }

  /**
   * The function `checkForBannedWords` checks for banned words in a given text using a specific method
   * and returns information about any banned words found.
   * @param text - The `text` parameter is the input text that you want to check for banned words.
   * @param foundryId - The `foundryId` parameter is likely an identifier or reference to a specific
   * foundry within the context of your application or system. It could be used to uniquely identify a
   * foundry or to perform operations specific to that foundry.
   * @param prevFilterWords - This has the info about preciously detected filtered words
   * @returns The function `checkForBannedWords` is returning the information about banned words found in
   * the `text` parameter using the `Misc.strikeThroughBadWords` function. The information is related to
   * the banned words, and it is returned as `bannedWordInfo`.
   */
  function checkForBannedWords(text, foundryId, prevFilterWords) {
    const bannedWordInfo = Misc.strikeThroughBadWords(
      text,
      store,
      'ai-companion',
      route?.name || '',
      foundryId,
      prevFilterWords?.warning || [],
      prevFilterWords?.banned || []
    );
    return bannedWordInfo;
  }

  /**
   * The function `checkForPriceInText` searches for price-related patterns in a given text and returns a
   * message indicating the presence of prices along with the matched price values.
   * @param text - It looks like you have provided a function `checkForPriceInText` that is designed to
   * extract price-related information from a given text. The function uses various regular expressions
   * to match different patterns that indicate the presence of prices in the text.
   * @returns The function `checkForPriceInText` returns a string that includes an error message from the
   * translation function `t` along with the cleaned and joined price matches found in the input text.
   * The error message is 'pages.aiTextEditor.errors.price_in_text' followed by the cleaned and joined
   * price matches separated by commas.
   */
  function checkForPriceInText(text) {
    const allMatches = [];

    // 1. Currency symbols + digits (any number with currency symbol)
    const symbolBeforeMatches = text.match(Common.REGEX_COLLECTIONS.PRICE_SYMBOL_BEFORE) || [];
    allMatches.push(...symbolBeforeMatches);

    const symbolAfterMatches = text.match(Common.REGEX_COLLECTIONS.PRICE_SYMBOL_AFTER) || [];
    allMatches.push(...symbolAfterMatches);

    // 2. Digits + currency words/codes (any number with currency name/code)
    const digitCurrencyMatches = text.match(Common.REGEX_COLLECTIONS.PRICE_DIGIT_CURRENCY) || [];
    allMatches.push(...digitCurrencyMatches);

    // 3. Written numbers (one to ten) + currency words/codes
    const writtenCurrencyMatches = text.match(Common.REGEX_COLLECTIONS.PRICE_WRITTEN_CURRENCY) || [];
    allMatches.push(...writtenCurrencyMatches);

    // 4. Promotional phrases with numbers or currency
    const promotionalNumberMatches = text.match(Common.REGEX_COLLECTIONS.PRICE_PROMOTIONAL_NUMBER) || [];
    allMatches.push(...promotionalNumberMatches);

    const promotionalCurrencyMatches = text.match(Common.REGEX_COLLECTIONS.PRICE_PROMOTIONAL_CURRENCY) || [];
    allMatches.push(...promotionalCurrencyMatches);

    // Remove duplicates and clean up matches
    const uniqueMatches = [...new Set(allMatches)];

    if (uniqueMatches.length > 0) {
      // Clean up matches by trimming whitespace
      const cleanedMatches = uniqueMatches.map(match => match.trim()).filter(Boolean);

      if (cleanedMatches.length > 0) {
        return t('pages.aiTextEditor.errors.price_in_text') + cleanedMatches.join(', ') + '.';
      }
    }
  }

  /**
   * The function `checkForContactInfoInText` checks for contact information such as phone numbers,
   * emails, and URLs in a given text and returns any errors found.
   * @param text - It seems like you were about to provide some text input for the
   * `checkForContactInfoInText` function. Please go ahead and provide the text so that I can assist you
   * further with checking for contact information in it.
   * @returns The function `checkForContactInfoInText` returns an array of error messages related to
   * contact information found in the input text.
   */
  function checkForContactInfoInText(text) {
    const error = [];
    const updatedText = text;
    
    // First, extract all URLs to exclude them from phone/email detection
    let urls = updatedText.match(Common.REGEX_COLLECTIONS.URL_PATTERN);
    let updatedTextWithoutUrls = updatedText;
    
    // Replace URLs with spaces to check for phone numbers
    urls?.forEach(url => {
      updatedTextWithoutUrls = updatedTextWithoutUrls.replace(url, ''.repeat(url.length));
    });
    
    // Create new RegExp instances to avoid stateful behavior
    const emailPattern = new RegExp(Common.REGEX_COLLECTIONS.EMAIL_PATTERN);
    const phonePattern = new RegExp(Common.REGEX_COLLECTIONS.PHONE_PATTERN);
    // Check for phone numbers in URL-free text
    if (phonePattern.test(updatedTextWithoutUrls)) {
      const phoneNumbers = sanitizeArray(updatedTextWithoutUrls.match(Common.REGEX_COLLECTIONS.PHONE_PATTERN));
      error.push(t('pages.aiTextEditor.errors.contact_details_phone') + phoneNumbers.join(', ') + '.');
    }
    const emails = sanitizeArray(updatedText.match(Common.REGEX_COLLECTIONS.EMAIL_PATTERN) || []);
    if (emailPattern.test(updatedText)) {
      error.push(t('pages.aiTextEditor.errors.contact_details_email') + emails.join(', ') + '.');
    }

    if (urls) {
      urls = urls.filter((url, i, allUrls) => !allUrls.some((otherUrl, j) => i !== j && otherUrl.includes(url)));
    }
    const nonEmailUrls = urls
      ? urls.filter(url => {
        if (!emails) return true;
        return !emails.some(email => email.includes(url));
      })
      : [];

    const forbiddenUrls = nonEmailUrls.filter(url => !Common.REGEX_COLLECTIONS.FORBIDDEN_URL_PATTERN.test(url));
    if (forbiddenUrls.length > 0) {
      error.push(t('pages.aiTextEditor.errors.contact_details_url') + sanitizeArray(forbiddenUrls).join(', ') + '.');
    }
    return error;
  }

  /**
   * The function `checkForFontFormats` checks a given text for font extensions and formats, flagging any
   * matches found.
   * @param text - The `checkForFontFormats` function takes a `text` parameter as input. This function is
   * designed to check for font formats and extensions within the provided text. It uses regular
   * expressions to identify font extensions, ignored font format names, and other font formats.
   * @returns The function `checkForFontFormats` returns an error message if any flagged font formats are
   * found in the input text. The error message includes the flagged font formats joined by a comma. If
   * no flagged font formats are found, it returns `null`.
   */
  function checkForFontFormats(text) {
    const extensionPattern = new RegExp(Common.REGEX_COLLECTIONS.FONT_EXTENSION_PATTERN, 'gi');
    const postfix = Common.IGNORED_FONT_FORMAT_NAMES_POSTFIX.join('|');
    const ignoredNames = Common.IGNORED_FONT_FORMAT_NAMES.map(name => `${name}(?!\\s+(${postfix}))`).join('|');

    const otherFontFormats = Common.FONT_FORMAT_NAMES.filter(
      name => !Common.IGNORED_FONT_FORMAT_NAMES.includes(name)
    ).join('|');

    const formatPattern = new RegExp(`\\b(${ignoredNames}|${otherFontFormats})\\b`, 'gi');

    const extensionMatches = text.match(extensionPattern) || [];
    const formatMatches = text.match(formatPattern) || [];

    const flaggedWords = [...new Set([...extensionMatches, ...formatMatches])];

    if (flaggedWords.length > 0) {
      const cleanedWords = flaggedWords.map(word => word.replace(/[<>]/g, '').trim());
      const uniqueWords = [...new Set(cleanedWords)].filter(Boolean);
      if (uniqueWords.length > 0) {
        return t('pages.aiTextEditor.errors.font_format_reference') + uniqueWords.join(', ') + '.';
      }
    }

    return null;
  }

  /**
   * The function `checkForFreeFonts` checks if the input text contains any free font patterns and
   * returns an error message if found.
   * @param text - The `checkForFreeFonts` function takes a `text` parameter as input. This function
   * checks if the `text` contains any free font patterns using a regular expression defined in
   * `Common.REGEX_COLLECTIONS.FREE_FONT_PATTERN`. If a free font pattern is found in the `text`, the
   * @returns The function `checkForFreeFonts` is returning the error message
   * `'pages.aiTextEditor.errors.free_fonts_not_allowed'` if the `FREE_FONT_PATTERN` regex test on the
   * input `text` returns true. Otherwise, it returns `null`.
   */
  function checkForFreeFonts(text) {
    if (Common.REGEX_COLLECTIONS.FREE_FONT_PATTERN.test(text)) {
      return t('pages.aiTextEditor.errors.free_fonts_not_allowed');
    }
    return null;
  }

  /**
   * The function `analyzeByWords` analyzes text by identifying words, determining their language, and
   * calculating the ratio of English words to total words in the text.
   * @param text - The `analyzeByWords` function you provided is quite complex and involves analyzing
   * text to determine the ratio of English words to non-English words. It uses various rules and
   * thresholds to make this determination based on the presence of stopwords in different languages.
   * @returns The function `analyzeByWords(text)` returns the ratio of English words to the total number
   * of words in the input text.
   */
  function analyzeByWords(text) {
    const { regex, stopwords, thresholds } = Common.LANGUAGE_ANALYSIS;
    const EN_STOPWORDS = new Set(stopwords.en);
    const FR_STOPWORDS = new Set(stopwords.fr);
    const DE_STOPWORDS = new Set(stopwords.de);
    const ES_STOPWORDS = new Set(stopwords.es);

    const words = [];
    let latinBuffer = '';
    let cjkBuffer = '';

    const isCjk = ch => regex.cjkScripts.test(ch);
    const isSeparator = ch => regex.separator.test(ch);

    const flushLatin = () => {
      if (latinBuffer) {
        words.push(latinBuffer.toLowerCase());
        latinBuffer = '';
      }
    };
    const flushCjk = () => {
      if (cjkBuffer) {
        words.push(cjkBuffer);
        cjkBuffer = '';
      }
    };

    for (const ch of text) {
      if (isCjk(ch)) {
        // Close any latin token and continue CJK accumulation
        flushLatin();
        cjkBuffer += ch;
        continue;
      }
      if (isSeparator(ch)) {
        // On separator, flush any open buffers
        flushLatin();
        flushCjk();
        continue;
      }
      // Non-CJK, non-separator => part of a latin-ish token
      flushCjk();
      latinBuffer += ch;
    }

    // Final flush
    flushLatin();
    flushCjk();

    // Compute stopword dominance on latin tokens to disambiguate ambiguous latin words
    const latinTokens = words.filter(w => regex.asciiLatin.test(w));
    const countMatches = (tokens, dict) => tokens.reduce((acc, w) => acc + (dict.has(w) ? 1 : 0), 0);
    const enStopCount = countMatches(latinTokens, EN_STOPWORDS);
    const frStopCount = countMatches(latinTokens, FR_STOPWORDS);
    const deStopCount = countMatches(latinTokens, DE_STOPWORDS);
    const esStopCount = countMatches(latinTokens, ES_STOPWORDS);

    const dominance = [
      { key: 'en', count: enStopCount },
      { key: 'fr', count: frStopCount },
      { key: 'de', count: deStopCount },
      { key: 'es', count: esStopCount }
    ].sort((a, b) => b.count - a.count);

    const dominant = dominance[0];
    const second = dominance[1];
    const dominantIsEnglish = dominant.key === 'en';
    const dominantHasSignal = dominant.count >= thresholds.dominanceMinCount && dominant.count > (second?.count || 0);
    const nonEnglishStopCount = frStopCount + deStopCount + esStopCount;
    const totalStopCount = enStopCount + nonEnglishStopCount;
    const nonEnglishStopDensity = totalStopCount > 0 ? nonEnglishStopCount / totalStopCount : 0;

    let treatAmbiguousLatinAsEnglish = false;
    let treatAmbiguousLatinAsNonEnglish = false;
    if (dominantHasSignal) {
      if (dominantIsEnglish) {
        treatAmbiguousLatinAsEnglish = true;
      } else {
        treatAmbiguousLatinAsNonEnglish = true;
      }
    }
    // Additional rule: if non-English stopwords are numerous and not fewer than English, bias to non-English
    if (!treatAmbiguousLatinAsNonEnglish && nonEnglishStopCount >= thresholds.nonEnglishStopMin && nonEnglishStopCount >= enStopCount) {
      treatAmbiguousLatinAsNonEnglish = true;
      treatAmbiguousLatinAsEnglish = false;
    }
    // Additional rule: if non-English stopword density is high, bias to non-English
    if (!treatAmbiguousLatinAsNonEnglish && nonEnglishStopDensity >= thresholds.nonEnglishStopDensityMin && nonEnglishStopCount >= thresholds.nonEnglishStopMin) {
      treatAmbiguousLatinAsNonEnglish = true;
      treatAmbiguousLatinAsEnglish = false;
    }

    let englishWordCount = 0, nonEnglishWordCount = 0;
    for (let word of words) {
      if (regex.asciiLatin.test(word)) {
        if (FR_STOPWORDS.has(word) || DE_STOPWORDS.has(word) || ES_STOPWORDS.has(word)) {
          nonEnglishWordCount++;
        } else if (EN_STOPWORDS.has(word)) {
          englishWordCount++;
        } else if (treatAmbiguousLatinAsNonEnglish) {
          nonEnglishWordCount++;
        } else if (treatAmbiguousLatinAsEnglish) {
          englishWordCount++;
        } else {
          // Default to English for ambiguous latin if no strong dominance signal
          englishWordCount++;
        }
      } else if (regex.letter.test(word)) {
        nonEnglishWordCount++;
      }
    }

    const total = englishWordCount + nonEnglishWordCount;
    return total > 0 ? englishWordCount / total : 0;
  }

  /**
   * The function `analyzeByChars` calculates the ratio of English characters to total characters in a
   * given text.
   * @param text - The `analyzeByChars` function takes a text input and analyzes the characters in the
   * text to determine the percentage of English characters present in the text.
   * @returns The function `analyzeByChars` returns the ratio of English characters to the total number
   * of characters in the input text.
   */
  function analyzeByChars(text) {
    const chars = [...text.normalize('NFC')];
    let englishChars = 0, nonEnglishChars = 0;

    for (let ch of chars) {
      if (!Common.LANGUAGE_ANALYSIS.regex.letter.test(ch)) continue;
      if (/[A-Za-z]/.test(ch)) englishChars++;
      else nonEnglishChars++;
    }

    const total = englishChars + nonEnglishChars;
    return total > 0 ? englishChars / total : 0;
  }

  /**
   * The function `analyzeLanguageContent` analyzes the language content of a given text by scoring it
   * based on words and characters, and then making a decision based on the scores to determine if
   * English content dominates.
   * @param text - The `analyzeLanguageContent` function takes a `text` parameter as input. This text is
   * then analyzed based on word and character scores to determine the dominance of English content. The
   * function calculates a decision score based on the minimum of word and character scores and compares
   * it against a threshold to determine if the
   * @returns The function `analyzeLanguageContent` returns an error message if the decision score is
   * less than a certain threshold, indicating that the text contains less English content. Otherwise, it
   * returns `null`.
   */
  function analyzeLanguageContent(text) {
    const wordScore = analyzeByWords(text);
    const charScore = analyzeByChars(text);

    // Decision: be conservative — require both views (word and char) to agree on English dominance
    // Using minimum penalizes cases where CJK characters are numerous but English words dominate token count
    const decisionScore = Math.min(wordScore, charScore);
    console.log('Language Analysis Scores:', { wordScore, charScore, decisionScore });

    return decisionScore < Common.LANGUAGE_ANALYSIS.thresholds.englishMinFraction
      ? t('pages.aiTextEditor.errors.less_english_content', {
        englishContent: (decisionScore * 100).toFixed(2)
      })
      : null;
  }

  function sanitizeArray(arr) {
    return [
      ...new Set(
        arr.filter(item => item !== null && item !== undefined && !(typeof item === 'string' && item.trim() === ''))
      )
    ];
  }

  return {
    checkForExtraSpaces,
    checkForBannedWords,
    checkMinMaxLength,
    checkForPriceInText,
    checkForContactInfoInText,
    checkForFontFormats,
    checkForFreeFonts,
    analyzeLanguageContent
  };
}
